import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Navigation state that asks the browse page to land with the pill bar at the
 * top of the viewport, where it will pin.
 *
 * Carried as router state rather than inferred from the route, because the
 * same page is reached both ways: a reader who clicked Spaces wants the wall
 * in front of them, and a reader who followed a link or a search result wants
 * the page from its beginning.
 */
export const SCROLL_TO_PILL_BAR_STATE = { scrollToPillBar: true } as const;

/** The sticky band this scrolls to, marked on the browse shell's own section. */
const PILL_BAR_SELECTOR = '[data-section="pill-bar"]';

/**
 * The band's own box, or the row inside it when the band has none.
 *
 * Above `md` the band is `display: contents` — deliberately, so the pill row
 * inside it sticks against the page column rather than against the band's own
 * two rows (see `DirectoryBrowsePage`). An element with `display: contents`
 * generates no box at all, so `scrollIntoView` on it has nothing to scroll to
 * and silently does nothing. Measured on the browse page, the band's first
 * element child there is that sticky row, which is the thing worth landing at
 * the top.
 *
 * Below `md` the band is a real sticky box and is its own answer, which is
 * what carries its `scroll-margin-top` past the header.
 */
const scrollTargetIn = (band: Element): Element | null =>
    band.getClientRects().length > 0 ? band : band.firstElementChild;

/**
 * Land a view switch with the pill bar at the top.
 *
 * Switching between Projects, Spaces and Styles replaces the whole listing,
 * so where the reader was in the old one means nothing in the new one. Left
 * alone they arrive above the fold of a page whose point is the grid, and have
 * to scroll past the heading they just came from to see what they asked for.
 *
 * Only for a switch the reader made: Back and Forward restore their own
 * position, and a plain visit to the page starts at the top.
 */
export const useScrollToPillBar = (): void => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const requested =
        (location.state as { scrollToPillBar?: boolean } | null)
            ?.scrollToPillBar === true;

    useEffect(() => {
        if (!requested || navigationType === 'POP') return;

        // After the frame the router's own scroll restoration writes in, so
        // this is the last word on where the page lands rather than a scroll
        // it undoes.
        const frame = requestAnimationFrame(() => {
            const band = document.querySelector(PILL_BAR_SELECTOR);
            const target = band && scrollTargetIn(band);
            if (!(target instanceof HTMLElement)) return;
            // The target is the sticky row, and a stuck sticky element
            // measures at its pinned position — scrollIntoView on it silently
            // goes nowhere when the reader is deep in the page, which is
            // exactly when this landing matters. Unstick it for a moment to
            // read where it actually sits in the page; this runs before the
            // frame paints, so nothing flashes.
            const inlinePosition = target.style.position;
            target.style.position = 'static';
            const staticTop =
                window.scrollY + target.getBoundingClientRect().top;
            target.style.position = inlinePosition;
            const scrollMargin =
                Number.parseFloat(getComputedStyle(target).scrollMarginTop) ||
                0;
            // Instant, not smooth: the listing under it has already been
            // replaced, so animating across it travels through a page that is
            // no longer the one the reader was looking at.
            window.scrollTo({
                top: Math.max(0, staticTop - scrollMargin),
                behavior: 'instant',
            });
        });
        return () => cancelAnimationFrame(frame);
        // `location.key` changes on every navigation, including one to the
        // view already showing.
    }, [requested, navigationType, location.key]);
};
